import React, { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Button,
    Drawer,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Divider,
    Fab,
    TablePagination
} from '@mui/material'
import {
    Add as AddIcon,
    FilterList as FilterIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    Dashboard as DashboardIcon,
    List as ListIcon,
    Edit as EditIcon
} from '@mui/icons-material'
import IssueForm from './IssueForm/IssueForm'
import IssueDetail from './IssueDetail/IssueDetail'
import IssueDashboard from './IssueDashboard/IssueDashboard'
import { Dialog, DialogContent, Autocomplete } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Snackbar, Alert } from '@mui/material'

const IssueMobileView = ({ logic }) => {
    const { state, setters, actions, utils, options, permissions } = logic
    const { can } = permissions
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [viewMode, setViewMode] = useState('list') // 'list' or 'dashboard'
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const {
        filteredIssues, selectedIssue, showForm, showDetail,
        editMode, statusFilter, priorityFilter, issueTypeFilter,

        plantFilter, categoryFilter, assignedToFilter, escalationFilter,
        dateRange, plantOptions, searchTerm, snackbar
    } = state

    const {
        handleCreateIssue, handleEditIssue, handleViewIssue,
        handleFormSubmit, handleFormClose,
        handleDetailClose, exportToCSV
    } = actions

    const {
        getPriorityColor, getStatusColor, getEscalationLevelStyle
    } = utils

    const {
        statusOptions, priorityOptions, issueTypeOptions,
        categoryOptions, assignedToOptions, escalationOptions
    } = options



    return (
        <Box sx={{ pb: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            {/* Mobile Header */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'white',
                p: 2,
                boxShadow: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" fontWeight="bold">
                        {viewMode === 'list' ? `Issues (${filteredIssues.length})` : 'Dashboard'}
                    </Typography>
                </Box>

                <Box>
                    <IconButton onClick={() => setViewMode(prev => prev === 'list' ? 'dashboard' : 'list')}>
                        {viewMode === 'list' ? <DashboardIcon color="primary" /> : <ListIcon color="primary" />}
                    </IconButton>
                    {viewMode === 'list' && (
                        <IconButton onClick={() => setFilterDrawerOpen(true)}>
                            <FilterIcon color="primary" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <Box sx={{ p: 2 }}>
                {viewMode === 'dashboard' ? (
                    <IssueDashboard issues={filteredIssues} />
                ) : (
                    <>
                        {/* Issue List */}
                        {filteredIssues
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((issue, index) => {
                                const esc = getEscalationLevelStyle(issue.escalationLevel)
                                const isDelayed = issue.delayDays > 0
                                const isResolved = issue.status === 'Resolved' || issue.status === 'Closed'
                                const isDeferred = issue.status === 'Deferred'

                                // Determine background color based on status
                                let bgColor = 'white'
                                let borderColor = 'none'

                                if (isResolved) {
                                    bgColor = '#e8f5e9' // Light green
                                    borderColor = '4px solid #66bb6a'
                                } else if (isDeferred) {
                                    bgColor = '#e0e0e0' // Darker grey
                                    borderColor = '4px solid #9e9e9e'
                                } else if (isDelayed) {
                                    bgColor = '#ffebee' // Light pink/red
                                    borderColor = '4px solid #ef5350'
                                }

                                return (
                                    <Card
                                        key={issue.id}
                                        sx={{
                                            mb: 2,
                                            borderRadius: 2,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                            bgcolor: bgColor,
                                            borderLeft: borderColor
                                        }}
                                        onClick={() => handleViewIssue(issue)}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            {/* Header with Status */}
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                    #{index + 1 + page * rowsPerPage}
                                                </Typography>
                                                <Chip
                                                    label={issue.status}
                                                    size="small"
                                                    color={getStatusColor(issue.status)}
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            </Box>

                                            {/* Title */}
                                            <Typography variant="subtitle1" fontWeight="600" lineHeight={1.3} mb={1}>
                                                {issue.title}
                                            </Typography>

                                            {/* Plant Name and Capacity */}
                                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {issue.plantName}
                                                </Typography>
                                                {issue.plantCapacity && (
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        bgcolor: '#f5f5f5',
                                                        px: 0.8,
                                                        py: 0.2,
                                                        borderRadius: 1
                                                    }}>
                                                        <b>{issue.plantCapacity}</b> kWp
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Issue Type */}
                                            {issue.issueType && (
                                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                                    Type: <strong>{issue.issueType}</strong>
                                                </Typography>
                                            )}

                                            {/* Closure Dates */}
                                            <Box display="flex" gap={2} mb={1.5} flexWrap="wrap">
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Expected Closure
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="500">
                                                        {issue.expectedClosingDate
                                                            ? new Date(issue.expectedClosingDate).toLocaleDateString('en-GB')
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Actual Closure
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="500">
                                                        {issue.actualClosingDate
                                                            ? new Date(issue.actualClosingDate).toLocaleDateString('en-GB')
                                                            : 'Pending'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Tags */}
                                            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                                <Chip
                                                    label={issue.priority}
                                                    size="small"
                                                    variant="outlined"
                                                    color={getPriorityColor(issue.priority)}
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                                <Chip
                                                    label={issue.category}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                                {issue.escalationLevel && (
                                                    <Chip
                                                        label={esc.label}
                                                        size="small"
                                                        color={esc.color}
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                                {isDelayed && (
                                                    <Chip
                                                        label={`${issue.delayDays}d overdue`}
                                                        size="small"
                                                        color="error"
                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Root Cause */}
                                            {issue.rootCausePreliminary && (
                                                <Box mt={1}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        RC: <strong>{issue.rootCausePreliminary}</strong>
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Assigned To */}
                                            {issue.assignedTo && (
                                                <Box mt={1}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Assigned To: <strong>{issue.assignedTo}</strong>
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                )
                            })}

                        {filteredIssues.length === 0 && (
                            <Box textAlign="center" mt={4} color="text.secondary">
                                <Typography>No issues found.</Typography>
                            </Box>
                        )}

                        <TablePagination
                            component="div"
                            count={filteredIssues.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10))
                                setPage(0)
                            }}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage="Rows:"
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                '& .MuiTablePagination-toolbar': {
                                    px: { xs: 0.5, sm: 2 },
                                    minHeight: 52,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                                    display: { xs: 'grid', sm: 'flex' },
                                    gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'unset' },
                                    alignItems: 'center',
                                },
                                '& .MuiTablePagination-spacer': {
                                    display: { xs: 'none', sm: 'block' },
                                },
                                '& .MuiTablePagination-selectLabel': {
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    mb: 0,
                                },
                                '& .MuiTablePagination-select': {
                                    fontSize: { xs: '0.72rem', sm: '0.875rem' },
                                    mr: { xs: 0.25, sm: 2 },
                                    lineHeight: 1,
                                },
                                '& .MuiTablePagination-selectRoot': {
                                    mr: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    gridColumn: { xs: '1', sm: 'unset' },
                                    '&::before': {
                                        content: '"Rows:"',
                                        display: { xs: 'inline-block', sm: 'none' },
                                        fontSize: '0.75rem',
                                        color: 'text.secondary',
                                        mr: 0.5,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1,
                                    },
                                },
                                '& .MuiTablePagination-displayedRows': {
                                    fontSize: { xs: '0.72rem', sm: '0.875rem' },
                                    ml: { xs: 0, sm: 'auto' },
                                    mr: { xs: 0.25, sm: 1 },
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                    gridColumn: { xs: '2', sm: 'unset' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    lineHeight: 1,
                                },
                                '& .MuiTablePagination-actions': {
                                    ml: 0,
                                    flexShrink: 0,
                                    gridColumn: { xs: '3', sm: 'unset' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& .MuiIconButton-root': {
                                        padding: { xs: '1px', sm: '8px' },
                                    }
                                }
                            }}
                        />
                    </>
                )}
            </Box>

            {/* Floating Action Buttons */}
            {viewMode === 'list' && (
                <>
                    {/* Download FAB - Available to all with view access */}
                    <Fab
                        color="primary"
                        aria-label="download"
                        onClick={exportToCSV}
                        size="medium"
                        sx={{
                            position: 'fixed',
                            bottom: 80,
                            right: 90,
                            zIndex: 1050
                        }}
                    >
                        <DownloadIcon />
                    </Fab>

                    {/* Create Issue FAB */}
                    {can('issue.create') && (
                        <Fab
                            color="primary"
                            aria-label="add"
                            onClick={handleCreateIssue}
                            size="medium"
                            sx={{
                                position: 'fixed',
                                bottom: 80, // Moved up for MobileBottomNav
                                right: 16,
                                zIndex: 1050
                            }}
                        >
                            <AddIcon />
                        </Fab>
                    )}
                </>
            )}

            {/* Filter Drawer */}
            <Drawer
                anchor="right"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            >
                <Box sx={{ p: 2, width: '85vw', maxWidth: 350 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Filters</Typography>
                        <IconButton onClick={() => setFilterDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                        {/* Plant Filter */}
                        <Autocomplete
                            multiple
                            size="small"
                            options={plantOptions}
                            getOptionLabel={(option) => option.name}
                            value={plantOptions.filter((plant) =>
                                plantFilter.includes(plant.id)
                            )}
                            onChange={(event, newValue) => {
                                setters.setPlantFilter(newValue.map((plant) => plant.id))
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Plant"
                                    placeholder="Select Plant"
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />

                        <TextField
                            size="small"
                            label="Search"
                            value={searchTerm}
                            onChange={(e) => setters.setSearchTerm(e.target.value)}
                        />

                        <FormControl size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setters.setStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {statusOptions.map(status => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={priorityFilter}
                                onChange={(e) => setters.setPriorityFilter(e.target.value)}
                                label="Priority"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {priorityOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={issueTypeFilter}
                                onChange={(e) => setters.setIssueTypeFilter(e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {issueTypeOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                onChange={(e) => setters.setCategoryFilter(e.target.value)}
                                label="Category"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {categoryOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Escalation</InputLabel>
                            <Select
                                value={escalationFilter}
                                onChange={(e) => setters.setEscalationFilter(e.target.value)}
                                label="Escalation"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {escalationOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel>Assigned To (Int.)</InputLabel>
                            <Select
                                value={assignedToFilter}
                                onChange={(e) => setters.setAssignedToFilter(e.target.value)}
                                label="Assigned To (Int.)"
                            >
                                <MenuItem value="all">All</MenuItem>
                                {assignedToOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From"
                                format="DD-MM-YYYY"
                                value={dateRange.from}
                                onChange={(date) => setters.setDateRange((prev) => ({ ...prev, from: date }))}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                            <DatePicker
                                label="To"
                                format="DD-MM-YYYY"
                                value={dateRange.to}
                                onChange={(date) => setters.setDateRange((prev) => ({ ...prev, to: date }))}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </LocalizationProvider>


                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setFilterDrawerOpen(false)}
                            sx={{ mt: 2 }}
                        >
                            Apply Filters
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            {/* Dialogs Reuse - Fullscreen for mobile */}
            <Dialog
                open={showForm}
                onClose={handleFormClose}
                fullScreen
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6">{editMode ? 'Edit Issue' : 'New Issue'}</Typography>
                    <IconButton onClick={handleFormClose}><CloseIcon /></IconButton>
                </Box>
                <DialogContent>
                    <IssueForm
                        issue={selectedIssue}
                        onSubmit={handleFormSubmit}
                        onCancel={handleFormClose}
                        categoryOptions={options.categoryOptions}
                        priorityOptions={options.priorityOptions}
                        statusOptions={options.statusOptions}
                        escalationOptions={options.escalationOptions}
                        issueTypeOptions={options.issueTypeOptions}
                        assignedToOptions={options.assignedToOptions}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={showDetail}
                onClose={handleDetailClose}
                fullScreen
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6">Details</Typography>
                    <IconButton onClick={handleDetailClose}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    {selectedIssue && (
                        <>
                            <IssueDetail
                                issue={selectedIssue}
                                onEdit={() => {
                                    handleDetailClose()
                                    handleEditIssue(selectedIssue)
                                }}
                                onClose={handleDetailClose}
                                setSnackbar={setters.setSnackbar}
                            />
                            {/* Floating Edit Button */}
                            {can('issue.update') && (
                                <Fab
                                    color="warning"
                                    aria-label="edit"
                                    sx={{
                                        position: 'fixed',
                                        bottom: 80, // Moved up for MobileBottomNav
                                        right: 16,
                                        bgcolor: '#ff9800',
                                        '&:hover': {
                                            bgcolor: '#f57c00'
                                        }
                                    }}
                                    onClick={() => {
                                        handleDetailClose()
                                        handleEditIssue(selectedIssue)
                                    }}
                                >
                                    <EditIcon />
                                </Fab>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={snackbar?.open}
                autoHideDuration={6000}
                onClose={() => setters.setSnackbar({ open: false, message: '', severity: 'success' })}
            >
                <Alert
                    onClose={() => setters.setSnackbar({ open: false, message: '', severity: 'success' })}
                    severity={snackbar?.severity || 'success'}
                    sx={{ width: '100%' }}
                >
                    {snackbar?.message}
                </Alert>
            </Snackbar>
        </Box >
    )
}

export default IssueMobileView
